import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { updateProfile } from '@/lib/api';
import { User, Loader2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    business_name: user?.business_name || '',
    business_type: user?.business_type || '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    try {
      await updateProfile(form);
      await refreshUser();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  return (
    <div className="animate-fade-in" data-testid="profile-page">
      <div className="mb-8">
        <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A] tracking-tight">Profile</h1>
        <p className="text-sm text-[#5C5C55] mt-1">Update your details</p>
      </div>

      <div className="bg-white rounded-2xl border border-[#E5E2DC] shadow-[0_2px_10px_rgba(0,0,0,0.02)] p-6 sm:p-8">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-14 h-14 rounded-full bg-[#E8EDE5] flex items-center justify-center text-xl font-heading font-medium text-[#3A4035]">
            {user?.name?.charAt(0)}
          </div>
          <div>
            <p className="font-heading text-lg font-medium text-[#1A1A1A]">{user?.name}</p>
            <p className="text-sm text-[#8A8B85]">{user?.email}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Full Name</Label>
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="bg-[#F2F0EB] border-transparent rounded-xl h-11 placeholder:text-[#8A8B85]" data-testid="profile-name" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Phone</Label>
            <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
              className="bg-[#F2F0EB] border-transparent rounded-xl h-11 placeholder:text-[#8A8B85]" data-testid="profile-phone" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Business Name</Label>
            <Input value={form.business_name} onChange={e => setForm(f => ({ ...f, business_name: e.target.value }))}
              className="bg-[#F2F0EB] border-transparent rounded-xl h-11 placeholder:text-[#8A8B85]" data-testid="profile-business-name" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Business Type</Label>
            <Input value={form.business_type} onChange={e => setForm(f => ({ ...f, business_type: e.target.value }))}
              className="bg-[#F2F0EB] border-transparent rounded-xl h-11 placeholder:text-[#8A8B85]" data-testid="profile-business-type" />
          </div>

          <Button type="submit" disabled={saving}
            className="w-full bg-[#3A4035] text-white rounded-full h-11 font-medium hover:bg-[#2B3027]"
            data-testid="save-profile-button">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />}
            {saved ? 'Saved!' : 'Save Changes'}
          </Button>
        </form>
      </div>
    </div>
  );
}
